import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Eye, EyeOff, Clock } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await register(email, password, name);
    
    if (result.success) {
      if (result.pending) {
        setSubmitted(true);
        toast.success('Registration submitted! Awaiting admin approval.');
      } else {
        toast.success('Account created successfully!');
        navigate('/');
      }
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  if (submitted) {
    return (
      <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
        <div className="w-full max-w-md">
          <div className="bg-[#121212] border border-zinc-800 p-8 text-center" data-testid="pending-approval-message">
            <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              Registration Submitted
            </h1>
            <p className="text-zinc-400 mb-6">
              Your account is pending admin approval. You will be able to login once an administrator approves your registration.
            </p>
            <Link to="/login">
              <Button 
                variant="outline"
                className="border-zinc-800 text-white hover:bg-zinc-900 rounded-none"
              >
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
      <div className="w-full max-w-md">
        <div className="bg-[#121212] border border-zinc-800 p-8" data-testid="register-form-container">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-2">
              Join A.C.C.T
            </h1>
            <p className="text-zinc-500 text-sm">
              Create your account and compete
            </p>
          </div>

          <div className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm">
            Note: All registrations require admin approval before you can login.
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-400 text-sm uppercase tracking-wide">
                Display Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your gaming alias"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 focus:ring-cyan-400"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400 text-sm uppercase tracking-wide">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 focus:ring-cyan-400"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 text-sm uppercase tracking-wide">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 pr-12 focus:border-cyan-400 focus:ring-cyan-400"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-400 text-sm uppercase tracking-wide">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 focus:ring-cyan-400"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="register-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Submit Registration
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300" data-testid="login-link">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
